import React, { type ReactNode, useEffect, useMemo } from 'react';

import { isIsolatedCloud } from '@atlaskit/atlassian-context/is-isolated-cloud';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { AncestorChoreographerProvider } from '@atlassian/ipm-choreographer/ancestor-choreographer-context';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import type { PostOfficeUserInteractionHandlers } from '@atlassian/post-office-analytics/types';
import {
	StableChoreographerProvider,
	useChoreographerContext,
} from '@atlassian/post-office-context/choreographer';
import {
	MessageContextProvider,
	type MessageCategory,
	type MessageContextData,
	PlacementProvider,
	useMessageViewed,
} from '@atlassian/post-office-context/message-lifecycle';

export type { MessageCategory } from '@atlassian/post-office-context/message-lifecycle';

export type PostOfficeMessageCreationType = 'implicit' | 'explicit';

export type PostOfficeMessageProps = {
	/**
	 * The unique ID for this specific delivery of the message.
	 * Used for choreographer concurrency control and viewed analytics.
	 */
	messageInstanceId: string;

	/**
	 * The ID of the message template.
	 * Used for choreographer message type resolution and analytics.
	 */
	messageTemplateId: string;

	/**
	 * The placement ID — used by viewed analytics to identify which placement
	 * this message was rendered in. This is the same `placementId` used in
	 * `usePlacementData`, `useMessageData`, etc.
	 */
	placementId: string;

	/**
	 * B3 trace id from the placement response that returned this message.
	 * Written onto placement context for the dropped message-instance event.
	 */
	traceId?: string;

	/**
	 * The message category — used by the choreographer to determine concurrency rules.
	 * Defaults to 'Engagement' if omitted.
	 */
	messageCategory?: MessageCategory;

	/** The trigger ID associated with this message instance. Used for message instance analytics. */
	triggerId?: string;

	/** Whether this message instance was created implicitly or explicitly. */
	messageCreationType?: PostOfficeMessageCreationType;

	/** Optional journey ID associated with this message instance. */
	journeyId?: string;

	/** Optional journey trigger ID associated with this message instance. */
	journeyTriggerId?: string;

	/** Optional config set ID associated with this message instance. */
	configSetId?: string;

	/**
	 * Bypass the Post Office choreographer for this message.
	 * Defaults to `false` and should almost always stay that way — disabling requires
	 * Post Office team approval.
	 */
	disableChoreographer?: boolean;

	/** Render `null` for tenants in Isolated Cloud environments. */
	hideInIsolatedCloud?: boolean;

	/**
	 * A render prop returning the message UI. Receives the Post Office user-interaction
	 * handlers, already bound to this message, so a placement can record what the user decided
	 * without wiring up analytics itself:
	 *
	 * ```tsx
	 * <PostOfficeMessage {...props}>
	 *   {({ onCtaActioned, onClosed }) => (
	 *     <MyBanner
	 *       onTry={() => onCtaActioned({ interactionIdentifier: 'tryIt' })}
	 *       onDismiss={() => onClosed({ interactionIdentifier: 'dismiss' })}
	 *     />
	 *   )}
	 * </PostOfficeMessage>
	 * ```
	 *
	 * @see https://developer.atlassian.com/platform/post-office/analytics-and-reporting/instrumenting-user-interactions/
	 */
	children: (interactions: PostOfficeUserInteractionHandlers) => ReactNode;
};

/**
 * Fires the Post Office "viewed" analytics events on mount.
 *
 * This is a separate inner component so that it only mounts when the choreographer
 * allows the message to render. It fires all three viewed events exactly once:
 *   - messageViewed() (legacy in-app lifecycle endpoint)
 *   - messageInstanceViewed()
 *   - messageInstanceDeliveredSuccessfully()
 *
 * Must be rendered inside a MessageContextProvider and PlacementProvider so that
 * useMessageViewed() can read the required context values.
 */
const ViewedOnMount = (): null => {
	const messageViewed = useMessageViewed();

	useEffect(() => {
		void messageViewed();
		// We intentionally omit deps — this should fire exactly once on mount.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

/**
 * Defers evaluation of the children render prop until the message is mounted.
 *
 * This ensures that any side effects in the render prop only execute when the
 * choreographer allows the message to render, not when it is blocked.
 */
const RenderedChildren = ({
	children,
}: {
	children: (interactions: PostOfficeUserInteractionHandlers) => ReactNode;
}): JSX.Element => {
	const interactions = usePostOfficeUserInteractions();

	return <>{children(interactions)}</>;
};

const ChoreographedMessage = ({
	children,
	messageId,
	useChoreographedRender,
}: React.PropsWithChildren<{
	messageId: string;
	useChoreographedRender: (args: { messageId: string }) => boolean;
}>): JSX.Element | null => {
	const shouldRender = useChoreographedRender({ messageId });

	if (!shouldRender) {
		return null;
	}

	// Post Office is actively choreographing this message. Signal it to descendants
	// so nested product/PO choreographer-wrapped components (e.g. JiraSpotlight, or
	// Confluence components built on the ipm-choreographer engine) defer instead of
	// starting a second, colliding choreographer over the same subtree.
	if (FeatureGates.checkGate('post-office_enable_choreographer-ancestor-context')) {
		return <AncestorChoreographerProvider>{children}</AncestorChoreographerProvider>;
	}

	return <>{children}</>;
};

/**
 * Render-time Isolated Cloud gate.
 *
 * Returns `null` when the prop is enabled AND the tenant is in an Isolated Cloud
 * environment. Mirrors the headful behaviour wired by
 * `createBasePlacementComponentV2` (`options.hideInIsolatedCloud`).
 *
 * SSR handling is delegated to `isIsolatedCloud()` itself, which falls back to
 * `globalThis.ssrContext?.isInIC` when `document` is undefined.
 */
const IsolatedCloudGate = ({
	children,
	hideInIsolatedCloud,
}: React.PropsWithChildren<{
	hideInIsolatedCloud?: boolean;
}>): JSX.Element | null => {
	if (hideInIsolatedCloud && isIsolatedCloud()) {
		return null;
	}

	return <>{children}</>;
};

/**
 * Choreographer gating using the `ChoreographerContext` provided by
 * `StableChoreographerProvider` (from `@atlassian/post-office-context/choreographer`).
 *
 * When the choreographer is disabled or its factory is not yet loaded,
 * the message renders unconditionally.
 *
 * When `disableChoreographer` is `true`, the gate short-circuits and renders
 * children unconditionally — bypassing the choreographer entirely for this message.
 */
const ChoreographerGate = ({
	children,
	messageId,
	messageTemplateId,
	messageCategory,
	disableChoreographer,
	awaitChoreographerFactory,
}: React.PropsWithChildren<{
	messageId: string;
	messageTemplateId: string;
	messageCategory?: MessageCategory;
	disableChoreographer?: boolean;
	awaitChoreographerFactory: boolean;
}>): JSX.Element | null => {
	// Hooks must run on every render to preserve hook order — do not short-circuit
	// before calling them, even when `disableChoreographer` is set.
	const { factory: choreographerFactory, choreographerEnabled } = useChoreographerContext();
	const isChoreographedPostOfficeMessagesEnabled = FeatureGates.checkGate(
		'enable_choreographed_post_office_messages',
	);

	if (disableChoreographer) {
		return <>{children}</>;
	}

	if (
		awaitChoreographerFactory &&
		choreographerEnabled &&
		isChoreographedPostOfficeMessagesEnabled &&
		!choreographerFactory
	) {
		return null;
	}

	if (choreographerFactory && choreographerEnabled && isChoreographedPostOfficeMessagesEnabled) {
		const { shouldBeChoreographed, useChoreographedRender } = choreographerFactory;

		const messageType = messageCategory
			? (messageCategory.toLowerCase() as 'engagement' | 'marketing' | 'transactional')
			: undefined;

		if (shouldBeChoreographed({ messageType })) {
			const useMessageTemplateIdAsChoreographerMessageId = FeatureGates.checkGate(
				'post-office_use_template_id_for_choreo',
			);
			const choreographerMessageId = useMessageTemplateIdAsChoreographerMessageId
				? messageTemplateId
				: messageId;

			return (
				<ChoreographedMessage
					messageId={choreographerMessageId}
					useChoreographedRender={useChoreographedRender}
				>
					{children}
				</ChoreographedMessage>
			);
		}
	}

	return <>{children}</>;
};

/**
 * Provides the choreographer context this component's gating depends on, so headless
 * consumers do not have to mount `StableChoreographerProvider` themselves.
 *
 * Nesting is safe: the underlying choreographer factory is a per-product singleton
 * held on `window`, so a provider mounted here resolves to the same factory (and
 * therefore the same fatigue/concurrency queue) as one mounted by an ancestor.
 *
 */
const ChoreographerProvider = ({
	children,
	placementId,
	enabled,
}: React.PropsWithChildren<{
	placementId: string;
	enabled: boolean;
}>): JSX.Element => {
	if (!enabled) {
		return <>{children}</>;
	}

	return (
		<StableChoreographerProvider placementId={placementId}>{children}</StableChoreographerProvider>
	);
};

/**
 * A stripped-down Post Office message wrapper for headless API consumers.
 *
 * Use this component to wrap your own UI message component when rendering
 * a Post Office message via the headless API (`usePlacementData`, `useMessageData`, etc.).
 *
 * It provides the minimal Post Office infrastructure required for in-product rendering:
 *
 * 1. **Choreographer gating** — integrates with the Post Office choreographer so that
 *    message fatigue and concurrency rules are respected. Returns `null` when the
 *    choreographer blocks this message.
 *
 * 2. **Viewed analytics** — automatically fires `messageViewed`, `messageInstanceViewed`,
 *    and `messageInstanceDeliveredSuccessfully` when the component mounts (i.e., when
 *    the choreographer allows the message to render).
 *
 * 3. **Choreographer context** — mounts `StableChoreographerProvider` itself, so consumers
 *    do not need to wrap this component in one. An ancestor provider (e.g. one mounted per
 *    placement) remains supported; both resolve to the same choreographer singleton.
 *
 * 4. **Interaction analytics** — passes the Post Office user-interaction handlers to the
 *    render prop, already bound to this message, so consumers record what the user decided
 *    through those rather than constructing click events themselves.
 *
 * **Prerequisites:** This component must be rendered inside the Post Office provider stack
 * (the same one the headless hooks rely on).
 *
 * Most consumers do not use this component directly — the `useMessageData` and
 * `useMessageInstances` hooks return a version with the placement and message already bound,
 * whose render prop supplies the message alongside the handlers.
 *
 * @example
 * ```tsx
 * <PostOfficeMessage
 *   messageInstanceId={messageInstanceId}
 *   messageTemplateId="my-template"
 *   placementId="my-placement"
 * >
 *   {({ onCtaActioned }) => (
 *     <MySpotlightComponent
 *       onTry={() => onCtaActioned({ interactionIdentifier: 'tryIt' })}
 *     />
 *   )}
 * </PostOfficeMessage>
 * ```
 */
export const PostOfficeMessage = ({
	messageInstanceId,
	messageTemplateId,
	placementId,
	traceId,
	messageCategory,
	triggerId,
	messageCreationType,
	journeyId,
	journeyTriggerId,
	configSetId,
	disableChoreographer,
	hideInIsolatedCloud,
	children,
}: PostOfficeMessageProps): JSX.Element | null => {
	const messageContext: MessageContextData = useMemo(
		() => ({
			messageInstanceId,
			messageTemplateId,
			messageCategory,
			triggerId,
			messageCreationType,
			journeyId,
			journeyTriggerId,
			configSetId,
		}),
		[
			messageInstanceId,
			messageTemplateId,
			messageCategory,
			triggerId,
			messageCreationType,
			journeyId,
			journeyTriggerId,
			configSetId,
		],
	);

	const placementContext = useMemo(() => ({ placementId, traceId }), [placementId, traceId]);

	const providesChoreographer =
		!disableChoreographer && FeatureGates.checkGate('post-office_enable_choreographer_provider');

	return (
		<ChoreographerProvider placementId={placementId} enabled={providesChoreographer}>
			<IsolatedCloudGate hideInIsolatedCloud={hideInIsolatedCloud}>
				<PlacementProvider value={placementContext}>
					<MessageContextProvider value={messageContext}>
						<ChoreographerGate
							messageId={messageInstanceId}
							messageTemplateId={messageTemplateId}
							messageCategory={messageCategory}
							disableChoreographer={disableChoreographer}
							awaitChoreographerFactory={providesChoreographer}
						>
							<ViewedOnMount />
							<RenderedChildren>{children}</RenderedChildren>
						</ChoreographerGate>
					</MessageContextProvider>
				</PlacementProvider>
			</IsolatedCloudGate>
		</ChoreographerProvider>
	);
};
